// 自定义 hook 用于管理基于 hash 状态
import {useCallback, useEffect, useRef, useState} from "react";

function encodeHash(hashState) {
    // 如果 hashState 不是对象或为 null，返回空字符串
    if (typeof hashState !== 'object' || hashState === null) {
        return '';
    }

    // 将对象转换为键值对数组，然后join成字符串
    const entries = Object.entries(hashState);
    const filtered = entries.filter(([key, value]) => {
        // 跳过 undefined 和 null
        if (value === undefined || value === null) {
            return false;
        }
        // 对于 daysFromAscan 和 hoursToClose，检查是否为空对象（min 和 max 都是 null/undefined）
        if (key === 'daysFromAscan' || key === 'hoursToClose') {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                if ((value.min == null || value.min === undefined) && 
                    (value.max == null || value.max === undefined)) {
                    return false; // 跳过空的范围对象
                }
            }
        }
        return true;
    });
    const result = filtered
        .map(([key, value]) => {
            // 编码键和值
            const encodedKey = encodeURIComponent(key);
            // 如果值是数组或对象，将其转换为 JSON 字符串
            const encodedValue = encodeURIComponent(
                (Array.isArray(value) || (typeof value === 'object' && value !== null)) 
                    ? JSON.stringify(value) 
                    : value,
            );
            return `${encodedKey}=${encodedValue}`;
        })
        .join('&');
    return result;
}

function decodeHash(hash) {
    // 如果 hash 是空的或不是字符串，返回空对象
    if (!hash || typeof hash !== 'string') {
        return {};
    }

    // 移除开头的 # 符号（如果存在）
    hash = hash.startsWith('#') ? hash.slice(1) : hash;

    // 将 hash 分割成键值对数组
    const pairs = hash.split('&');

    // 使用 reduce 方法将键值对数组转换为对象
    return pairs.reduce((result, pair) => {
        // 分割每个键值对
        const [key, value] = pair.split('=');
        if (!key) return result;

        // 如果值是空字符串，跳过（不添加到结果中）
        if (value === '' || value === undefined) {
            return result;
        }

        // 解码键和值（处理 URL 编码的特殊字符）
        const decodedKey = decodeURIComponent(key);
        const decodedValue = decodeURIComponent(value || '');

        try {
            // 尝试解析值为 JSON，如果失败则使用原始值
            const parsedValue = JSON.parse(decodedValue);
            // 这些字段在 GraphQL 中期望是字符串类型，不应该被转换为数字
            const stringFields = [
                'trackingNumbers', 'channelId', 'channelOrderNumber',
                'sortingCodeSegment1', 'sortingCodeSegment2', 'sortingCodeSegment3',
                'overallStatus', 'podReviewStatus', 'exceptionReasonCode',
                'nextAction', 'exceptionHubId', 'exceptionWarehouseId','pickupHubId',
                'voucherClearStatus','settlementItemCode','settlementClientId','clientId','activeStatus',
                'operatorId','timezone'
            ];
            if (stringFields.includes(decodedKey)) {
                // 对于字符串字段，如果解析后是数字，保持原始字符串值
                if (typeof parsedValue === 'number') {
                    result[decodedKey] = decodedValue;
                } else {
                    result[decodedKey] = parsedValue;
                }
            } else {
                // 对于其他字段，使用解析后的值
                result[decodedKey] = parsedValue;
            }
        } catch (e) {
            result[decodedKey] = decodedValue;
        }

        return result;
    }, {});
}


// 自定义 hook 用于管理基于 hash 状态
export const useHashState = (initialState) => {

    const [hashState, setHashState] = useState(() => {
        const hash = window.location.hash.slice(1);
        return hash ? decodeHash(hash) : initialState;
    });

    const isInitialMount = useRef(true);

    const handleHashChange = useCallback(() => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            try {
                const newState = decodeHash(hash);
                setHashState(newState);
            } catch (e) {
                console.error("Error parsing hash:", e);
            }
        } else {
            setHashState(initialState);
        }
    }, [initialState]);

    useEffect(() => {
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [handleHashChange]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return; // 初始挂载时跳过更新 URL hash，避免用 initialState 覆盖现有 URL hash
        }
        
        const hashValue = encodeHash(hashState);
        const currentHash = window.location.hash.slice(1);
        
        // 只有当编码后的 hash 值与当前 URL hash 不同时才更新
        if (hashValue !== currentHash) {
            window.history.replaceState(null, '', `#${hashValue}`);
        }
    }, [hashState]);

    useEffect(() => {
        const hash = getTmpHistoryBackHash();
        if (hash) {
            setHashState(decodeHash(hash));
        }
    }, []);

    const updateHashState = useCallback((newHashState) => {
        if (typeof newHashState === 'function') {
            setHashState(prev => {
                const updatedState = newHashState(prev);
                // 简单合并状态，不删除 undefined 字段（由 encodeHash 过滤）
                return {...prev, ...updatedState};
            });
        } else if (typeof newHashState === 'object' && newHashState !== null) {
            setHashState(prev => {
                // 简单合并状态，不删除 undefined 字段（由 encodeHash 过滤）
                return {...prev, ...newHashState};
            });
        } else {
            console.error("Hash state must be an object or a function that returns an object");
            throw new Error("Invalid hash state update");
        }
    }, []);

    return [hashState, updateHashState];
};


// 临时历史记录的 key
const HISTORY_BACK_HASH_KEY = '_TMP_HISTORY_BACK_HASH_';

// 设置临时历史记录
export const setTmpHistoryBackHash = (hash) => {
    sessionStorage.setItem(HISTORY_BACK_HASH_KEY, hash);
    console.log("setTmpHistoryBackHash:", hash);
};

// 获取临时历史记录
// 用于解决history.back()无法携带hash的问题
export const getTmpHistoryBackHash = () => {
    const hash = sessionStorage.getItem(HISTORY_BACK_HASH_KEY);
    sessionStorage.removeItem(HISTORY_BACK_HASH_KEY);
    return hash;
};
